<template>
	<div class="sales-chart container-fluid py-3">
		<!-- Filter UI -->
		<div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-2">
			<div class="filters mb-4 d-flex flex-wrap align-items-center">
				<div class="form-inline mb-2 mb-md-0">
					<label class="mr-2 font-weight-bold">Zeitraum:</label>
					<input type="date" v-model="parameter.from" class="form-control form-control-sm mr-2" />
					<span class="mr-2">–</span>
					<input type="date" v-model="parameter.to" class="form-control form-control-sm mr-3" />
				</div>

				<div class="d-flex justify-content-center" style="max-height: calc(1.5em + 0.5rem + 2px); !important;">

					<button @click="showFilters = !showFilters" class="btn btn-outline-secondary btn-sm">
						Filter {{showFilters ? 'verbergen' : 'anzeigen'}}
					</button>

					<div
							v-if="showFilters"
							class="filter-panel d-flex mx-2"
					>
						<!-- 4 multiselects -->

							<multi-select-wrapper
									:value="parameter.filter.countries"
									@input="parameter.filter.countries = $event"
									:options="countries"
									:placeholder="'Länder'"
									class="mx-2"
									style="max-width: 200px;"
							/>

						<multi-select-wrapper
								:value="parameter.filter.marketplaces"
								@input="parameter.filter.marketplaces = $event"
								:options="marketAccounts"
								:placeholder="'Marktplätze'"
								class="mx-2"
								style="max-width: 200px;"
						/>

						<multi-select-wrapper
								:value="parameter.filter.vendorIds"
								@input="parameter.filter.vendorIds = $event"
								:options="allVendors"
								:placeholder="'Lieferanten'"
								class="mx-2"
								style="max-width: 200px;"
						/>

						<multi-select-wrapper
								:value="parameter.filter.mainVendorIds"
								@input="parameter.filter.mainVendorIds = $event"
								:options="mainVendors"
								:placeholder="'Hauptlieferanten'"
								class="mx-2"
								style="max-width: 200px;"	/>

						<button @click="resetFilters()" class="btn btn-outline-secondary btn-sm" style="min-width: 130px !important;">
							Filter zurücksetzten
						</button>
					</div>
				</div>

				<div class="form-inline mb-2 mb-md-0">
					<b-dropdown id="dropdown-1" :text="'Auswertung: nach ' + metricText" class="m-md-2" size="sm">
						<b-dropdown-item @click="parameter.metric = 'marketplaces'">Nach Verkäuferkonto</b-dropdown-item>
						<b-dropdown-item @click="parameter.metric = 'country'">Nach Land</b-dropdown-item>
						<b-dropdown-item @click="parameter.metric = 'vendor'">Nach Lieferant</b-dropdown-item>
						<b-dropdown-item @click="parameter.metric = 'mainVendor'">Nach Hauptlieferant</b-dropdown-item>
					</b-dropdown>

					<b-btn @click="loadData" class="btn btn-sm" variant="primary" :disabled="requestInProgress">
						Aktualisieren
					</b-btn>
				</div>
			</div>
			<div>
				<b-btn @click="generateExcelFile()" class="btn btn-sm" variant="primary" :disabled="!hasDataAvailable || requestInProgress" >
					Excel Export
				</b-btn>
			</div>
		</div>

		<!-- Chart container -->
		<div class="card shadow-sm">
			<div class="card-body">
				<div class="chart-container" style="position: relative; height: 70vh;">
					<Line
							id="my-chart-id"
							:options="chartOptions"
							:data="chartData"
							class="w-100 h-100"
							ref="salesChart"
							v-if="!requestInProgress"
							:key="chartKey"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import MultiSelectWrapper from "./util/MultiSelectWrapper.vue";
import { Line } from 'vue-chartjs';
import { Chart as ChartJS, Title, Tooltip, Legend, LineElement, PointElement, CategoryScale, LinearScale } from 'chart.js';
ChartJS.register(Title, Tooltip, Legend, LineElement, PointElement, CategoryScale, LinearScale);



export default {
	name: "SalesOverviewIndex",
	components: { Line, MultiSelectWrapper },
	props: {
		countries: {
			type: Array,
			default: () => []
		},
		marketAccounts: {
			type: Array,
			default: () => []
		},
		allVendors: {
			type: Array,
			default: () => []
		},
		mainVendors: {
			type: Array,
			default: () => []
		}
	},
	data() {
		return {
			chartData: {
				labels: [],
				datasets: []
			},
			chartOptions: {
				responsive: true,
				maintainAspectRatio: false,
				scales: {
					y: {
						beginAtZero: true
					}
				},
				plugins: {
					legend: {
						onClick: this.handleLegendClick,
					},
				},
			},
			parameter:{
				from: null,
				to: null,
				metric: 'marketplaces',
				filter:{
					countries: [],
					marketplaces: [],
					vendorIds: [],
					mainVendorIds: []
				}
			},
			showFilters: false,
			groupedDailySales: [],
			groupOptions: [],
			selectedGroups: [],
			requestInProgress: false,
			chartKey: 0,
			showAllGraph: true,
		}
	},
	computed: {
		metricText() {
			switch(this.parameter.metric) {
				case 'marketplaces':
					return 'Marktplätze'
				case 'country':
					return 'Länder'
				case 'vendor':
					return 'Lieferanten'
				case 'mainVendor':
					return 'Hauptlieferanten'
				default:
					return 'unbekannter Wert'
			}
		},
		hasDataAvailable() {
			return this.groupedDailySales?.length > 0;
		}
	},
	watch: {
		selectedGroups() {
			this.setChartData();
			this.chartKey = Date.now();
		},
		chartData: {
			handler() {
				this.chartKey = Date.now();
				console.log('Chart data updated, new key:', this.chartKey);
			},
			deep: true
		}
	},
	methods: {
		loadData() {
			this.requestInProgress = true

			axios.post('/salesoverview', this.parameter)
				.then(res => {
					console.log(res)
					this.selectedGroups = [];
					this.groupedDailySales = res.data;
					if(!this.groupedDailySales){
						this.groupedDailySales = [];
					}
					// Extract all unique group labels
					this.groupOptions = [
						...new Set(this.groupedDailySales.flatMap(day => day.groups.map(g => g.label)))
					];

					this.setChartData();

					this.requestInProgress = false
				})
				.catch(error => {
					console.error('Error fetching sales overview data:', error)
					this.requestInProgress = false
				})
		},
		setChartData(){
			//this snipped resets the chart data and prevents an error
			// from appearing when switching between total and grouped views
			this.chartData = {
				labels: [],
				datasets: []
			};

			if (!this.groupedDailySales || this.groupedDailySales.length <= 0) {
				this.chartKey = Date.now();
				return;
			}

			const selected = this.selectedGroups || []; // example: ['abc','efg']

			// Filter to selected ones if any
			const activeLabels = selected.length > 0
					? this.groupOptions.filter(label => selected.includes(label))
					: this.groupOptions;

			// Set chart x-axis labels (the days)
			this.chartData.labels = this.groupedDailySales.map(day =>
					moment(day.date).locale('de').format('dd D.M.YYYY'));

			// Build one dataset per group label
			this.chartData.datasets = activeLabels.map(label => {
				const data = this.groupedDailySales.map(day => {
					const group = day.groups.find(g => g.label === label);
					return group ? group.orders : 0;
				});

				return {
					label,
					data,
					borderColor: this.getColorForLabel(label),
					borderWidth: 2,
					tension: 0.3,
					pointRadius: 4,
					pointHoverRadius: 6,
					fill: false,
				};
			});
		},
		addOrRemoveGroup(label) {
			if (label === 'Gesamt') {
				// special case → clear all selections
				this.selectedGroups = [];
				return;
			}
			const index = this.selectedGroups.indexOf(label);
			if (index === -1) {
				this.selectedGroups.push(label);
			} else {
				this.selectedGroups.splice(index, 1);
			}
		},
		// Generate a consistent color for each label
		getColorForLabel(label) {
			let hash = 0;
			for (let i = 0; i < label.length; i++) {
				hash = label.charCodeAt(i) + ((hash << 5) - hash);
			}
			// convert hash → 0–360 hue
			const hue = Math.abs(hash) % 360;
			return `hsl(${hue}, 90%, 40%)`;
		},
		generateExcelFile() {
			const mappedData = this.groupedDailySales.map(day => {
				const row = { Datum: day.date };
				this.groupOptions
						.filter(label => label !== 'Gesamt')
						.forEach(label => {
							const found = day.groups.find(g => g.label === label);
							row[label] = found ? found.orders : 0;
						});
				return row;
			});

			let metricText = "";
			switch(this.parameter.metric) {
				case 'marketplaces':
				case 'country':
					metricText = 'Marktplätze';
					break;
				case 'vendor':
				case 'mainVendor':
					metricText =  'Lieferanten';
					break;
				default:
					return 'unbekannter Wert'
			}
			let from = moment(this.parameter.from).format('DD-MM-YYYY');
			let to = moment(this.parameter.to).format('DD-MM-YYYY');

			Vue.nextTick().then(() => {
				const ws = XLSX.utils.json_to_sheet(mappedData);
				const wb = XLSX.utils.book_new();
				XLSX.utils.book_append_sheet(wb, ws);
				XLSX.writeFile(wb,  "Umsatz-" + metricText + "-" + from + "_"+ to + ".xlsx");
			})
		},
		handleLegendClick(e, legendItem, legend) {
			const chart = legend.chart;
			const index = legendItem.datasetIndex;
			const now = Date.now();

			// Track previous click info
			if (!chart._lastClick) chart._lastClick = { time: 0, index: null };
			const sameItem = chart._lastClick.index === index;
			const withinTime = now - chart._lastClick.time < 300;
			const doubleClicked = sameItem && withinTime;

			chart._lastClick = { time: now, index };

			const allDatasets = chart.data.datasets;

			if (doubleClicked) {
				if (!this.showAllGraph) {
					//all isolated already → show all again
					allDatasets.forEach((_, i) => chart.setDatasetVisibility(i, true));
					this.showAllGraph = true;
				} else {
					//isolate clicked dataset
					allDatasets.forEach((_, i) => chart.setDatasetVisibility(i, i === index));
					this.showAllGraph = false;
				}
			} else {
				//normal single-click toggle
				const visible = chart.isDatasetVisible(index);
				chart.setDatasetVisibility(index, !visible);
			}

			chart.update();
		},
		resetFilters() {
			this.parameter.filter.countries = [];
			this.parameter.filter.marketplaces = [];
			this.parameter.filter.vendorIds = [];
			this.parameter.filter.mainVendorIds = [];
		}
	},
	created() {
		const date = new Date();
		date.setDate(date.getDate() - 7);
		// Date one week ago
		this.parameter.from = date.toISOString().substring(0, 10)
		// Today's date
		this.parameter.to = new Date().toISOString().substring(0, 10)


		this.loadData()
	}
}

</script>

<style>
.scrollable-dropdown {
	max-height: 200px;
	overflow-y: auto;
}
</style>